import { useEffect, useRef, useState } from 'react';
import { MAX_ATTENDEES, MAX_ROOMS, MIN_ATTENDEES, MIN_ROOMS, MINIMUM_GUEST_ROOMS_COUNT } from '../../constants';
import { RoomsAndEventSpace, Variant } from './RoomsAndEventSpace.types';
import { isNumberWithinRange } from '../../utils';

const DEFAULT_VALUES: RoomsAndEventSpace = { rooms: 0, eventAttendees: 0 };

export const useRoomsAndEventSpace = (
  value: RoomsAndEventSpace | undefined,
  onChange: (data: RoomsAndEventSpace) => void,
  variant: Variant,
  isDesktop = true
) => {
  const [roomsAndEventSpace, setRoomsAndEventSpace] = useState<RoomsAndEventSpace>(() => ({
    ...DEFAULT_VALUES,
    ...value,
  }));
  const prevVariantRef = useRef<Variant>(variant);
  const isDesktopRoomsOnlyVariant = isDesktop && variant === Variant.ROOMS;

  const updateRoomsAndEventSpace = (updatedValue: RoomsAndEventSpace) => {
    setRoomsAndEventSpace(updatedValue);
    onChange(updatedValue);
  };

  useEffect(() => {
    if (value?.rooms !== roomsAndEventSpace.rooms || value?.eventAttendees !== roomsAndEventSpace.eventAttendees) {
      setRoomsAndEventSpace({ ...DEFAULT_VALUES, ...value });
    }
  }, [value?.rooms, value?.eventAttendees]);

  useEffect(() => {
    if (prevVariantRef.current !== variant) {
      updateRoomsAndEventSpace(DEFAULT_VALUES);
      prevVariantRef.current = variant;
    }
  }, [variant]);

  const updateRoomCount = (nextRoomCount: number) => {
    updateRoomsAndEventSpace({ ...(value ?? DEFAULT_VALUES), rooms: nextRoomCount });
  };

  const updateAttendeesCount = (nextAttendeesCount: number) => {
    updateRoomsAndEventSpace({ ...(value ?? DEFAULT_VALUES), eventAttendees: nextAttendeesCount });
  };

  const getNextRoomCount = (current: number, action: 'increment' | 'decrement'): number => {
    if (action === 'increment') {
      const nextCount = current + 1;
      return isDesktopRoomsOnlyVariant ? Math.max(MINIMUM_GUEST_ROOMS_COUNT, nextCount) : nextCount;
    }
    return Math.max(0, current - 1);
  };

  const increaseRooms = () => updateRoomCount(getNextRoomCount(value?.rooms ?? 0, 'increment'));
  const decreaseRooms = () => updateRoomCount(getNextRoomCount(value?.rooms ?? 0, 'decrement'));

  const increaseAttendees = () => updateAttendeesCount(Math.min(MAX_ATTENDEES, (value?.eventAttendees ?? 0) + 1));
  const decreaseAttendees = () => updateAttendeesCount(Math.max(MIN_ATTENDEES, (value?.eventAttendees ?? 0) - 1));

  const inputRoomHandler = (inputValue: number) => {
    if (isNumberWithinRange(inputValue, MIN_ROOMS, MAX_ROOMS)) {
      updateRoomCount(inputValue);
    }
  };

  const inputAttendeesHandler = (inputValue: number) => {
    if (isNumberWithinRange(inputValue, MIN_ATTENDEES, MAX_ATTENDEES)) {
      updateAttendeesCount(inputValue);
    }
  };

  const handleReset = () => updateRoomsAndEventSpace(DEFAULT_VALUES);

  return {
    roomsAndEventSpace,
    increaseRooms,
    decreaseRooms,
    increaseAttendees,
    decreaseAttendees,
    inputRoomHandler,
    inputAttendeesHandler,
    handleReset,
  };
};
